/** @file One dirty frame: pose, the tool's gizmo, chunks, renderer draw (background only without a rig). */
import { color } from '@pose2d/math/color';
import type { OverlayOptions } from '@pose2d/overlay/types';
import { cameraOf } from './cameraOf';
import { drawGizmo } from './drawGizmo';
import { poseInputOf } from './poseInputOf';
import type { RuntimeState } from './runtimeState';
import type { Scene } from './scene/createScene';
import { frameChunks } from './scene/frameChunks';
import { onionPoses } from './scene/onionPoses';
import { overlayOptionsFor } from './scene/overlayOptionsFor';
import type { PageSet } from './scene/pageSet';
import { poseScene } from './scene/poseScene';
import { selectedIndices } from './selectedIndices';
import { toolContextOf } from './toolContextOf';
import { toolPointers } from './toolPointers';
import type { ToolContext, ToolPointer } from './viewportTypes';
import { viewportStore } from './viewportStore';

const BACKGROUND = color.parseHex('#25282cff');

/** The viewport toggles as OverlayOptions, the active tool's extra layers merged over them (W3-34). */
function optionsOf(ctx: ToolContext, pointer: ToolPointer, animating: boolean): OverlayOptions {
  const base = overlayOptionsFor(viewportStore.getState().toggles, ctx.doc.view.grid, selectedIndices(ctx.maps, ctx.selection), animating);
  const extra = pointer.overlay?.(ctx);
  return extra === undefined ? base : { ...base, ...extra, selection: { ...base.selection, ...extra.selection } };
}

/** Poses the scene (R-E1-04/05), draws the idle or gesture gizmo, and fills `s.chunks` (the ghost included). */
function fillChunks(s: RuntimeState, scene: Scene, pages: PageSet, ctx: ToolContext): void {
  const input = poseInputOf(s, scene.data);
  poseScene(scene, input);
  const pointer = toolPointers[ctx.tool.active];
  const drawing = s.gesture === null ? pointer.idle(ctx) : s.gizmo;
  const gizmoChunks = drawing === null ? 0 : drawGizmo(scene.gizmo, drawing, ctx.camera, pages.textures, s.deps.font);
  const anim = input.at.kind === 'animation' ? input.at.anim : -1;
  const options = optionsOf(ctx, pointer, anim >= 0);
  const onion = options.onion === undefined ? [] : onionPoses(scene, anim, ctx.playhead.frame, ctx.doc.meta.fps);
  frameChunks(scene, { textures: pages.textures, camera: ctx.camera, options, font: s.deps.font, onionPoses: onion, gizmoChunks, ghost: s.ghost }, s.chunks);
}

/** Draws when dirty and sized; returns whether it drew (R-E1-02). */
export function drawFrame(s: RuntimeState): boolean {
  if (!s.dirty || s.width <= 0 || s.height <= 0) return false;
  s.dirty = false;
  const camera = cameraOf(s);
  const ctx = toolContextOf(s, camera);
  if (ctx === null || s.scene === null || s.pages === null) {
    s.deps.renderer.draw([], camera, BACKGROUND);
    return true;
  }
  fillChunks(s, s.scene, s.pages, ctx);
  s.deps.renderer.draw(s.chunks, camera, BACKGROUND);
  return true;
}
